import PcmRecorder from './PcmRecorder';

// Event listener type definitions
type EventListener = (...args: any[]) => void;
type EventMap = {
    onStopped: EventListener;
    onRecordingStarted: EventListener;
    onError: EventListener;
};

/**
 * Standalone static PCM recorder API wrapper that uses PcmRecorder for WAV PCM 16000 format recording.
 * Ensures that only one instance of the recorder is used at a time.
 * 
 * It will be used from within react components, but initialized in the root layout in a static way
 */
export class SharedRecorder {
    private static instance: SharedRecorder;
    private micPermission: PermissionState = 'prompt'; // 'granted' | 'denied' | 'prompt'
    private selectedInputDevice: string | null = null; // Must be updated externally on an interval
    private recordedBlob: Blob | null = null;
    private chunksAvailable: boolean = false;
    private format: string = 'wav'; // Always WAV PCM 16000 with PcmRecorder

    // Event listeners storage
    private eventListeners: Map<keyof EventMap, Set<EventListener>> = new Map();

    private constructor() {
        this.recordedBlob = null;
        this.initializeEventListeners();
    }

    private initializeEventListeners() {
        this.eventListeners.set('onStopped', new Set());
        this.eventListeners.set('onRecordingStarted', new Set());
        this.eventListeners.set('onError', new Set());
    }

    public static getInstance(): SharedRecorder {
        if (!SharedRecorder.instance) {
            SharedRecorder.instance = new SharedRecorder();
        }
        return SharedRecorder.instance;
    }

    // Event subscription methods
    public addEventListener<K extends keyof EventMap>(event: K, listener: EventMap[K]): void {
        const listeners = this.eventListeners.get(event);
        if (listeners) {
            listeners.add(listener);
        }
    }

    public removeEventListener<K extends keyof EventMap>(event: K, listener: EventMap[K]): void {
        const listeners = this.eventListeners.get(event);
        if (listeners) {
            listeners.delete(listener);
        }
    }

    private emitEvent<K extends keyof EventMap>(event: K): void {
        const listeners = this.eventListeners.get(event);
        if (listeners) {
            listeners.forEach(listener => {
                try {
                    listener();
                } catch (error) {
                    console.error(`Error in event listener for ${event}:`, error);
                }
            });
        }
    }

    public initialize({selectedInputDevice, format, timeout = 10000}:{selectedInputDevice: string, format?: string, timeout: number}) {
        this.selectedInputDevice = selectedInputDevice;
        // Note: format parameter is ignored as PcmRecorder always outputs WAV PCM 16000
        this.format = 'wav';

        this.wakeupOnMicPermissionGranted({timeout});
    }

    public async checkMicPermission(): Promise<PermissionState> {
        const micResult = await navigator.permissions.query({ name: 'microphone' as PermissionName });
        this.micPermission = micResult.state;

        return this.micPermission;
    }

    /**
     * Will wait and block for up to timeout milliseconds until the microphone permission is granted
     * It will check the permission every 300ms for up to timeout milliseconds
     * @param timeout
     */
    public async wakeupOnMicPermissionGranted({timeout = 3000}: {timeout?: number} = {}) {
        // console.log('SharedRecorder', 'wakeupOnMicPermissionGranted', 'timeout:', timeout);        

        if (this.micPermission === 'granted') {
            await this.wakeup();
        } else {            
            return new Promise((resolve, reject) => {
                const timer = setInterval(async () => {
                    await this.checkMicPermission();
                    if (this.micPermission === 'granted') {
                        clearInterval(timer);
                        await this.wakeup();
                        resolve(true);
                    }
                }, 500);            

                setTimeout(() => {
                    clearInterval(timer);
                    reject(new Error('Timeout waiting for microphone permission'));
                }, timeout);
            });
        }
    }

    /**
     * Sometimes, another recorder needs to be used (for agent calls), so we need to temporarily stop the shared recorder
     * Note: a short delay is needed before the shared recorder can be used again
     */
    public async sleep() {
        try {
            if (PcmRecorder.state === 'recording') {
                await PcmRecorder.stop();
                this.recordedBlob = PcmRecorder.getBlob();
                this.chunksAvailable = true;
                this.emitEvent('onStopped');
            }
            await PcmRecorder.sleep();
        } catch (error) {
            console.error('SharedRecorder sleep error:', error);
            this.emitEvent('onError');
        }
    }

    public async wakeup() {
        try {
            if (PcmRecorder.state === 'sleeping' || PcmRecorder.state === 'idle') {
                await PcmRecorder.wake();
            } else {
                console.info('SharedRecorder', 'wakeup called, but PcmRecorder is already awake');
            }
        } catch (error) {
            console.error('SharedRecorder wakeup error:', error);
            this.emitEvent('onError');
        }
    }

    // Gets called sometimes when a page deregisters in the useEffect return function
    public async handleLeaveView({viewName}: {viewName?: string} = {}) {
        // So far no need to do anything here
    }

    public async reset({timeout = 7000}: {timeout?: number} = {}) {
        this.clearChunks();
        this.micPermission = 'prompt';

        try {
            if (PcmRecorder.state === 'recording') {
                await PcmRecorder.stop();
            }
            await PcmRecorder.sleep();
        } catch (error) {
            console.error('SharedRecorder reset error:', error);
        }

        await this.checkMicPermission();
        await this.wakeupOnMicPermissionGranted({timeout});
    }

    public setSelectedInputDevice(selectedInputDevice: string) {
        this.selectedInputDevice = selectedInputDevice;
    }

    public getChunks(): BlobPart[] {
        // For backwards compatibility, return the blob as a single chunk if available
        return this.recordedBlob ? [this.recordedBlob] : [];
    }

    public getChunksAvailable(): boolean {
        return this.chunksAvailable;
    }

    public getBlob(): Blob | null {
        return this.recordedBlob;
    }

    public clearChunks() {
        this.recordedBlob = null;
        this.chunksAvailable = false;
    }

    public async startRecording() {
        try {
            if (PcmRecorder.state === 'recording') {
                console.info('SharedRecorder', 'startRecording called, but PcmRecorder is already recording');
                await PcmRecorder.stop();
                // Drop the payload in this case (do not emit onStopped event)
                await new Promise(resolve => setTimeout(resolve, 100));
            }

            // Ensure recorder is awake
            if (PcmRecorder.state === 'sleeping') {
                await PcmRecorder.wake();
            }

            this.clearChunks();
            await PcmRecorder.start();
            this.emitEvent('onRecordingStarted');
        } catch (error) {
            console.error('SharedRecorder startRecording error:', error);
            this.emitEvent('onError');
        }
    }

    public async stopRecording() {
        try {
            if (PcmRecorder.state === 'recording') {
                this.recordedBlob = await PcmRecorder.stop();
                this.chunksAvailable = true;
                this.emitEvent('onStopped');
            } else {
                console.info('SharedRecorder', 'stopRecording called, but PcmRecorder is not recording');
            }
        } catch (error) {
            console.error('SharedRecorder stopRecording error:', error);
            this.emitEvent('onError');
        }
    }
}